import React, { useState, useRef, useEffect } from 'react';
import { MdExpandMore } from 'react-icons/md';

export interface SelectOption {
  label: string;
  value: number;
}

interface Props {
  value: number;
  options: SelectOption[];
  onChange: (v: number) => void;
}

const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

/** Month options — 1-indexed (value 1=January … 12=December) */
export const monthOptions = (): SelectOption[] =>
  MONTHS_FULL.map((label, i) => ({ label, value: i + 1 }));

/** Month options — 0-indexed (value 0=January … 11=December) for mini-calendars */
export const monthOptions0 = (): SelectOption[] =>
  MONTHS_FULL.map((label, i) => ({ label, value: i }));

/** Year options from start to end inclusive */
export const yearOptions = (start: number, end: number): SelectOption[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i).map(y => ({
    label: String(y),
    value: y,
  }));

const CustomSelect: React.FC<Props> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll selected option into view when dropdown opens
  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('[data-selected="true"]') as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  return (
    <div ref={rootRef} className="relative select-none">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-9 flex items-center gap-0.5 border border-gray-200 rounded-xl px-3 bg-white text-sm text-gray-700 font-medium hover:border-gray-300 hover:bg-gray-50/60 focus:outline-none focus:ring-2 focus:ring-[#2D328F]/20 focus:border-[#2D328F]/40 transition-colors whitespace-nowrap"
      >
        {selected?.label ?? '—'}
        <MdExpandMore
          className={`text-gray-400 text-lg flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Animated dropdown */}
      <div
        style={{ transformOrigin: 'top center' }}
        className={[
          'absolute top-full left-0 mt-1.5 min-w-full bg-white border border-gray-100',
          'rounded-xl shadow-lg shadow-gray-200/80 z-50 overflow-hidden',
          'transition-all duration-200 ease-out',
          open
            ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-y-95 -translate-y-1 pointer-events-none',
        ].join(' ')}
      >
        <div ref={listRef} className="max-h-52 overflow-y-auto py-1">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              data-selected={opt.value === value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                opt.value === value
                  ? 'text-[#2D328F] font-semibold bg-[#2D328F]/5'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomSelect;
